/**
 * Registrar Incauto — migración web de ares5010.
 *
 * Busca un préstamo activo, carga cliente/vehículo, sugiere el valor del
 * incauto (10% de las cuotas vencidas impagas) y captura al incautador.
 * Guardar requiere AUTORIZACIÓN de un administrador: se crea la solicitud
 * en la tabla `autorizaciones` y se hace polling hasta que la aprueben o
 * declinen (equivalente web del listener de Firebase del escritorio).
 * Si el préstamo tiene Pre-Incauto (ares5030), el incautador viene fijado
 * y el pre-incauto se cierra al guardar.
 */
import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { imprimirActaIncauto } from "modules/incautos/impresionincauto";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Formatea cédula dominicana como 000-0000000-0.
const formatCedula = (v) => {
  const d = String(v || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 10);
  const p3 = d.slice(10, 11);
  let out = p1;
  if (p2) out += "-" + p2;
  if (p3) out += "-" + p3;
  return out;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

const DATOS_INICIAL = {
  cedula: "",
  nombres: "",
  apellidos: "",
  chasis: "",
  marca: "",
  modelo: "",
  colore: "",
  costo: 0,
  precio: 0,
  tienepreincauto: false,
};

export default function RegistrarIncauto({ embedded = false, onExit, onSaved, noprestInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const hoy = new Date().toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // ---- Préstamos activos (carga completa, filtro local) ----
  const [opts, setOpts] = useState([]);
  // Interruptores de Configuración (varinicial): incautador por catálogo y
  // registrar sin esperar la autorización del AdmG.
  const [cfg, setCfg] = useState({ catalogoincautadores: false, incautosinautorizacion: false });
  const [buscando, setBuscando] = useState(false);
  const [prestamoSel, setPrestamoSel] = useState(null);

  useEffect(() => {
    let activo = true;
    setBuscando(true);
    post("/api/incauto_prestamos", {})
      .then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.prestamos)) setOpts(data.prestamos);
        if (activo && ok && data.config) setCfg(data.config);
      })
      .finally(() => activo && setBuscando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Datos del préstamo ----
  const [datos, setDatos] = useState(DATOS_INICIAL);
  const [cargando, setCargando] = useState(false);
  const [valor, setValor] = useState("");
  const [cedincautador, setCedincautador] = useState("");
  const [nombreincautador, setNombreincautador] = useState("");

  // ---- Catálogo de incautadores (solo activos; no se escriben a mano) ----
  const [incautadores, setIncautadores] = useState([]);
  const [cargandoInc, setCargandoInc] = useState(false);
  useEffect(() => {
    let activo = true;
    setCargandoInc(true);
    post("/api/incautadores_listar", { soloactivos: true })
      .then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.incautadores)) setIncautadores(data.incautadores);
      })
      .finally(() => activo && setCargandoInc(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const incautadorSel =
    incautadores.find(
      (i) => (i.cedula || "").replace(/\D/g, "") === cedincautador.replace(/\D/g, "")
    ) || null;

  // Preselección: al abrir desde el expediente o una solicitud de incauto ya
  // viene el préstamo elegido. Se carga directo, sin buscarlo en `opts`: esa
  // lista solo trae la sucursal del usuario y un préstamo de otra sucursal
  // (p. ej. en un lote) se quedaba sin cargar.
  useEffect(() => {
    if (!noprestInicial) return;
    const id = String(noprestInicial);
    setPrestamoSel({ noprest: id, nombres: "", cedula: "", chasis: "" });
    cargarDatos(id).then((d) => {
      if (d) {
        setPrestamoSel({
          noprest: id,
          nombres: [d.nombres, d.apellidos].filter(Boolean).join(" "),
          cedula: d.cedula || "",
          chasis: d.chasis || "",
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noprestInicial]);

  const cargarDatos = async (noprest) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/incauto_datos", { noprest: String(noprest) });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cargar el préstamo", color: "error" });
        setPrestamoSel(null);
        setDatos(DATOS_INICIAL);
        return null;
      }
      setDatos(data);
      setValor(data.valorsugerido ? String(data.valorsugerido) : "");
      setCedincautador(formatCedula(data.cedincautador || ""));
      setNombreincautador(data.incautador || "");
      if (data.autorizadoporlote) {
        showAlert({
          message: `Aprobado en la solicitud de incauto #${data.lote}: no requiere otra autorización`,
          color: "success",
        });
      } else if (data.lotependiente) {
        showAlert({
          message: `Este préstamo está en la solicitud #${data.lote}, pendiente de aprobación del AdmG`,
          color: "warning",
        });
      } else if (data.tienepreincauto) {
        showAlert({
          message: "Este préstamo tiene un Pre-Incauto registrado — el incautador viene fijado",
          color: "warning",
        });
      }
      return data;
    } finally {
      setCargando(false);
    }
  };

  // ---- Autorización (polling sobre la tabla autorizaciones) ----
  const [autOpen, setAutOpen] = useState(false);
  const [autIdtext, setAutIdtext] = useState("");
  const [autStatus, setAutStatus] = useState(""); // "" | Progreso | Aprobado | Cancelado
  const [autComentario, setAutComentario] = useState("");
  const pollRef = useRef(null);

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => detenerPolling, []);

  const solicitarAutorizacion = async () => {
    const noprest = prestamoSel?.noprest;
    const { ok, data } = await post("/api/incauto_solicitarautorizacion", {
      datos: {
        descripcion: "Solicito autorizacion para realizar un incauto",
        noprest,
        cedula: datos.cedula,
        nombre: datos.nombres,
        apellido: datos.apellidos,
        chasis: datos.chasis,
        marca: datos.marca,
        modelo: datos.modelo,
        color: datos.colore,
        costo: datos.costo,
        precio: datos.precio,
        valorincauto: valor,
        incautador: nombreincautador,
        cedincautador,
      },
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo solicitar la autorización", color: "error" });
      return;
    }
    setAutIdtext(data.idtext);
    setAutStatus("Progreso");
    detenerPolling();
    pollRef.current = setInterval(async () => {
      const r = await post("/api/incauto_estadoautorizacion", { idtext: data.idtext });
      if (!r.ok) return;
      const st = r.data.status;
      if (st === "Aprobado") {
        detenerPolling();
        setAutStatus("Aprobado");
      } else if (st === "Cancelado" || st === "Declinado") {
        detenerPolling();
        setAutStatus("Cancelado");
        setAutComentario(r.data.comentario || "");
      }
    }, 5000);
  };

  const cancelarAutorizacion = async () => {
    detenerPolling();
    if (autIdtext && autStatus === "Progreso") {
      await post("/api/incauto_cancelarautorizacion", { idtext: autIdtext });
    }
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    detenerPolling();
    setPrestamoSel(null);
    setDatos(DATOS_INICIAL);
    setValor("");
    setCedincautador("");
    setNombreincautador("");
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  const validar = () => {
    if (!prestamoSel?.noprest) {
      showAlert({ message: "Seleccione un préstamo", color: "warning" });
      return false;
    }
    if (cfg.catalogoincautadores && !datos.tienepreincauto) {
      if (!cedincautador.trim() || !nombreincautador.trim()) {
        showAlert({ message: "Seleccione el incautador", color: "warning" });
        return false;
      }
    } else {
      if (cedincautador.replace(/\D/g, "").length !== 11) {
        showAlert({ message: "La cédula del incautador parece estar incorrecta", color: "warning" });
        return false;
      }
      if (!nombreincautador.trim()) {
        showAlert({ message: "El nombre del incautador no puede estar en blanco", color: "warning" });
        return false;
      }
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "El valor del incauto no puede estar en 0", color: "warning" });
      return false;
    }
    return true;
  };

  const guardar = async (autorizacionid = "") => {
    if (!validar()) return;
    setGuardando(true);
    try {
      const { ok, status, data } = await post("/api/incauto_guardar", {
        noprest: String(prestamoSel.noprest),
        cedincautador: cedincautador.trim(),
        nombreincautador: nombreincautador.trim(),
        valor: toNum(valor),
        autorizacionid,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Datos Guardados", color: "success" });
        if (data.recibo) imprimirActaIncauto(data.recibo);
        setAutOpen(false);
        limpiar();
        // recargar préstamos (el incautado ya no debe ofrecerse de nuevo con incauto abierto)
        post("/api/incauto_prestamos", {}).then(({ ok: ok2, data: d2 }) => {
          if (ok2 && Array.isArray(d2.prestamos)) setOpts(d2.prestamos);
        });
        if (onSaved) onSaved();
      } else if (status === 409 && data.requiereAutorizacion) {
        setAutOpen(true);
      } else {
        showAlert({ message: data.Error || "Error al guardar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registrar Incauto"
        subtitle={
          cfg.incautosinautorizacion
            ? "Incautación del vehículo de un préstamo en mora"
            : "Incautación del vehículo de un préstamo en mora — requiere autorización de un administrador"
        }
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => guardar(autStatus === "Aprobado" ? autIdtext : "")}
              disabled={guardando || !prestamoSel}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Chip
            label={`Fecha: ${hoy}`}
            sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
          />
          {datos.tienepreincauto && (
            <Chip
              label="Pre-Incauto registrado"
              sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
            />
          )}
          {autStatus === "Aprobado" && (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label="Autorización aprobada"
              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
            />
          )}
        </Stack>

        {/* Préstamo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.info}>
            Préstamo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={
                  prestamoSel && !opts.some((x) => String(x.noprest) === String(prestamoSel.noprest))
                    ? [prestamoSel, ...opts]
                    : opts
                }
                loading={buscando || cargando}
                value={prestamoSel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => String(o.noprest || o) === String(v.noprest || v)}
                onChange={(_, v) => {
                  setPrestamoSel(v);
                  if (v?.noprest) cargarDatos(v.noprest);
                  else setDatos(DATOS_INICIAL);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.nombres, o.cedula, o.chasis].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="No. de préstamo, cliente o chasis" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Cédula"
                value={datos.cedula}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cliente"
                value={[datos.nombres, datos.apellidos].filter(Boolean).join(" ")}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Vehículo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
            Vehículo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Chasis"
                value={datos.chasis}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Marca"
                value={datos.marca || ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Modelo"
                value={datos.modelo || ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Color"
                value={datos.colore || ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                fullWidth
                label="Precio"
                value={datos.precio ? fmtMoney(datos.precio) : ""}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Incautador y valor */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Incautador y Valor
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              {datos.tienepreincauto ? (
                <TextField
                  fullWidth
                  label="Incautador (fijado por el Pre-Incauto)"
                  value={[nombreincautador, cedincautador].filter(Boolean).join(" — ")}
                  InputProps={{ readOnly: true }}
                  sx={fpInputSx}
                />
              ) : !cfg.catalogoincautadores ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Cédula del incautador"
                    value={cedincautador}
                    onChange={(e) => setCedincautador(formatCedula(e.target.value))}
                    placeholder="000-0000000-0"
                    inputProps={{ maxLength: 13 }}
                    sx={fpInputSx}
                  />
                  <TextField
                    fullWidth
                    label="Nombre del incautador"
                    value={nombreincautador}
                    onChange={(e) => setNombreincautador(e.target.value)}
                    inputProps={{ maxLength: 45 }}
                    sx={fpInputSx}
                  />
                </Stack>
              ) : (
                <Autocomplete
                  options={incautadores}
                  value={incautadorSel}
                  loading={cargandoInc}
                  onChange={(_, v) => {
                    setCedincautador(v ? v.cedula || "" : "");
                    setNombreincautador(v ? v.nombres || "" : "");
                  }}
                  getOptionLabel={(o) => `${o.nombres || ""} — ${o.cedula || ""}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  noOptionsText={
                    incautadores.length
                      ? "Ningún incautador coincide"
                      : "No hay incautadores activos. Agréguelos en Incautos y Cargos → Incautadores"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Incautador"
                      placeholder="Buscar por nombre o cédula"
                      sx={fpInputSx}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Valor del incauto"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                helperText="Sugerido: 10% de las cuotas vencidas impagas"
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Autorización */}
      <Dialog
        open={autOpen}
        onClose={() => {}}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <GppMaybeOutlinedIcon sx={{ color: "#E65100" }} />
          Autorización requerida
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            Para realizar un incauto debe solicitar autorización a un administrador.
          </DialogContentText>
          {autStatus === "" && (
            <Button
              fullWidth
              onClick={solicitarAutorizacion}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Enviar solicitud de autorización
            </Button>
          )}
          {autStatus === "Progreso" && (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
              <CircularProgress size={28} />
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.85rem", color: FP.color.textSecondary }}
              >
                Esperando aprobación del administrador...
              </Typography>
            </Stack>
          )}
          {autStatus === "Aprobado" && (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#2E7D32" }} />
              <Typography sx={{ fontWeight: 800, color: "#2E7D32" }}>
                ¡Autorización aprobada!
              </Typography>
              <Button
                fullWidth
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={() => guardar(autIdtext)}
                disabled={guardando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Registrar incauto
              </Button>
            </Stack>
          )}
          {autStatus === "Cancelado" && (
            <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
              <Typography sx={{ fontWeight: 800, color: FP.color.danger }}>
                Solicitud declinada
              </Typography>
              {autComentario && (
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                  {autComentario}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cancelarAutorizacion} sx={btnGhost}>
            {autStatus === "Progreso" ? "Cancelar solicitud" : "Cerrar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
